import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { CREATION_CALENDAR_SELECTION } from '../../services/creation-calendar-selection';
import { ItemChangesComponent } from '@modules/team-item-changes/item-changes.component';
import { ItemReviewComponent } from '@modules/common/components/item-review/item-review.component';
import {
  ITEM_REVIEW_INLINE_HOST,
  type ItemReviewTarget,
} from '@modules/common/components/item-review/item-review-dialog.service';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { TabsComponent } from '@modules/common/components/tabs/tabs.component';
import { CalendarTenantScopeService } from '@modules/calendar-app/services/calendar-tenant-scope.service';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { input, linkedSignal, viewChild } from '@angular/core';
import { ItemHandoverComponent } from '@modules/common/components/item-handover/item-handover.component';
import {
  ITEM_HANDOVER_INLINE_HOST,
  type ItemHandoverTarget,
} from '@modules/common/components/item-handover/item-handover-dialog.service';
import { UILocations } from '@modules/common/types/uiLocations';
import { itemDisplayIdentity } from '@modules/common/components/item-workspace/item-display-identity';
import { itemChangeRevision } from '@modules/common/components/item-workspace/item-change-revision';
import {
  ItemQuickCommentComponent,
  type ItemFooterTab,
} from '@modules/comments/components/item-quick-comment/item-quick-comment.component';
import { ItemWorkspaceChipComponent } from '@modules/common/components/item-workspace/item-workspace-chip.component';
import { ItemWorkspaceAccessComponent } from '@modules/common/components/item-workspace/item-workspace-access.component';
import { ItemTeamLabelComponent } from '@modules/common/components/item-workspace/item-team-label.component';
import { WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { ItemWorkspaceFooterComponent } from '@modules/common/components/item-workspace/item-workspace-footer.component';
import { APP_CRUMBS, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { TitleByEventsFilterPipe } from '@modules/calendar-app/pipes/title-by-events-filter.pipe';
import { itemWorkspace } from '@modules/common/components/item-workspace/item-workspace-scope';
import {
  Component,
  ElementRef,
  ChangeDetectorRef,
  computed,
  effect,
  inject,
  Injector,
  Input,
  type OnChanges,
  OnInit,
  signal,
  type SimpleChanges,
  untracked,
} from '@angular/core';
import { headerLocationBadge } from '@modules/common/utils/header-location-badge';
import { Application } from '@modules/common/types/application';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { CalendarSummariesCache } from '@modules/calendar-app/services/calendar-summaries.cache';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { type FormArray, type FormControl, FormGroup } from '@angular/forms';
import { AccountService } from '@frontendlib/common/services/account.service';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { warmUpObservable } from '@frontendlib/common/decorators/warm-up-observable';
import { AccountTimeZoneSettingsService } from '@modules/account/services/account-timezone-settings.service';
import { CalendarsService } from '@modules/calendar-app/services/calendars.service';
import { DefaultReminderService } from '@modules/calendar-app/services/default-reminder.service';
import { EventInviteConfirmationService } from '@modules/calendar-app/services/event-invite-confirmation.service';
import { EventInviteService } from '@modules/calendar-app/services/event-invite.service';
import { EVENT_FORM_READ_CANCELLED, EventsService } from '@modules/calendar-app/services/events.service';
import { RecurrenceEditModalService } from '@modules/calendar-app/services/recurrence-edit-modal.service';
import { CalendarAppStateService } from '@modules/calendar-app/services/state.service';
import { Calendar } from '@modules/calendar-app/types/calendar';
import {
  CalendarEvent,
  type CalendarEventFormGroupOptions,
  type CalendarEventGroupConfig,
} from '@modules/calendar-app/types/calendar-event';
import {
  normalizeEmail,
  findMyParticipant,
  resolveMyRsvpStatus,
  resolveRsvpLifecycle,
  resolveRsvpStatusPrefix,
  type EventRsvpLifecycle,
  type RsvpTargetEvent,
} from '@modules/calendar-app/utils/event-rsvp.util';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { Tab } from '@modules/common/types/tab';
import { DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { TimeZoneSeedingController } from '@modules/form-controls/services/timezone-seeding.controller';
import { type ReminderFormGroup } from '@modules/form-controls/types/reminder';
import { toFakeLocal } from '@modules/form-controls/types/timezone-convert';
import type { AccountAppSubjectsType } from '@sharedlib/common/permissions/subjects/AccountAppSubjects';
import type { Actions } from '@sharedlib/common/permissions/types/Permissions';
import { Implements } from '@sharedlib/common/lang/Implements';
import { isValidTimeZone } from '@sharedlib/common/utils/timezone';
import { Objects } from '@sharedlib/common/lang/Objects';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { canEditDocumentsIn } from '@modules/files/version-history/file-edit-authority';
import { CalendarUtils } from '@sharedlib/dtos/helpers/calendar-app/CalendarUtils';
import type { EventParticipantDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { RecurrenceEditMode } from '@sharedlib/dtos/dto/calendar-app/recurrence-edit-mode';
import { getIdForSupplementaryData } from '@sharedlib/dtos/dto/StitchItemSupplementaryData';
import { defaultIfEmpty, EMPTY, Observable, of, skip, Subject, throwError } from 'rxjs';
import { catchError, filter, finalize, map, scan, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { participantCapValidator } from '../../validators/participant-cap.validator';
import { eventHeaderWhen } from '../../utils/event-header-when.util';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';

type EventFormRawValue = ReturnType<FormGroup<CalendarEventGroupConfig>['getRawValue']>;

@Component({
  selector: 'app-event-form',
  templateUrl: './event-form.component.html',
  styleUrls: ['./event-form.component.less'],
  standalone: false,
  providers: [
    // Invitations need the window's workspace client, including its tenant interceptor.
    EventInviteService,
    EventInviteConfirmationService,
    {
      provide: ITEM_REVIEW_INLINE_HOST,
      useFactory: () => {
        const form = inject(EventFormComponent);
        return { open: (target: ItemReviewTarget): boolean => form.openInlineReview(target) };
      },
    },
    WorkspaceIdentityService,
    {
      provide: ITEM_HANDOVER_INLINE_HOST,
      useFactory: () => {
        const form = inject(EventFormComponent);
        return { open: (target: ItemHandoverTarget): boolean => form.openInlineAssignment(target) };
      },
    },
  ],
})
export class EventFormComponent
  extends FullFormBaseComponent<StitchType.EVENT, CalendarEvent, CalendarEventGroupConfig>
  implements OnInit, OnChanges
{
  protected readonly UILocations = UILocations;
  public readonly insideModal = input(false);
  public readonly tabsOverflow = input<'dropdown' | 'scroll'>('dropdown');
  public readonly reviewApi = inject(EventsService).reviewApi;
  private readonly reviewControl = viewChild(ItemReviewComponent);
  protected readonly reviewContentVersion = itemChangeRevision(this.itemUpdated.pipe(map(() => undefined)));
  public openInlineReview(target: ItemReviewTarget): boolean {
    const control = this.reviewControl();
    if (
      target.stitchType !== StitchType.EVENT ||
      !this.insideModal() ||
      !control ||
      target.itemId !== this.handoverItemId ||
      target.tenantId !== this.handoverTenantId
    )
      return false;
    control.openRequest();
    return true;
  }

  public readonly handoverApi = inject(EventsService).handoverApi;
  protected readonly StitchType = StitchType;
  private readonly handoverControl = viewChild(ItemHandoverComponent);
  /** A hand-over is a team feature: a personal login gets none of its controls, whichever space is open. */
  private readonly accountKind = inject(AccountKindService);
  private readonly handoverPermissions: PermissionsService<Actions, AccountAppSubjectsType> =
    inject(PermissionsService);
  protected get handoverTenantId(): string | undefined {
    return this.accountKind.isTeam() && itemWorkspace(this.workspaceIdentity(), this.event) === 'team'
      ? this.event.accountId
      : undefined;
  }
  protected get handoverItemId(): string | undefined {
    return this.event.recurrentSeriesId ?? this.event.id;
  }
  protected get handoverCanAssign(): boolean {
    return canEditDocumentsIn(this.handoverPermissions, this.handoverTenantId);
  }
  protected get hubEntityUid(): string | undefined {
    return this.event?.id ? getIdForSupplementaryData(this.event) : undefined;
  }
  public openInlineAssignment(target: ItemHandoverTarget): boolean {
    const control = this.handoverControl();
    if (
      !this.handoverCanAssign ||
      !this.insideModal() ||
      !control ||
      target.itemId !== this.handoverItemId ||
      target.tenantId !== this.handoverTenantId
    )
      return false;
    control.openAssign();
    return true;
  }
  protected readonly workspaceRevision = itemChangeRevision(this.stitchItemChanged);

  protected readonly workspaceComponents = {
    'app-item-changes': ItemChangesComponent,
    'app-item-team-label': ItemTeamLabelComponent,
    'app-item-workspace-chip': ItemWorkspaceChipComponent,
    'app-item-workspace-access': ItemWorkspaceAccessComponent,
    'app-item-quick-comment': ItemQuickCommentComponent,
    'app-item-workspace-footer': ItemWorkspaceFooterComponent,
  };

  protected readonly sharingTenantId = signal<string | null>(null);
  private readonly workspaceIdentitySettled = signal(false);
  private readonly calendarTenantScope = inject(CalendarTenantScopeService, { optional: true });
  private readonly itemIdentityService = inject(WorkspaceIdentityService);
  protected readonly workspaceIdentity = toSignal(
    this.stitchItemChanged.pipe(
      skip(1),
      take(1),
      switchMap(() =>
        this.spaceContextStore.type() === SpaceType.SHARED_SPACE
          ? this.itemIdentityService.current().pipe(
              take(1),
              defaultIfEmpty(undefined),
              catchError(() => of(undefined)),
            )
          : of(undefined),
      ),
      tap((identity) => {
        this.workspaceIdentitySettled.set(true);
        if (!this.needsNewCalendar) return;
        if (!this.stitchItem?.id) {
          if (!identity) {
            const tenantId = this.event.accountId ?? this.calendarTenantScope?.effectiveTenantId();
            this.sharingTenantId.set(tenantId ?? null);
            if (tenantId) this.seedNewCalendar(tenantId, this.event.calendarId);
            else this.newCalendarStatus.set('error');
            return;
          }
          if (this.event?.accountId) {
            this.sharingTenantId.set(this.event.accountId);
            this.seedNewCalendar(this.event.accountId, this.event.calendarId);
          } else {
            const tenantId = this.calendarTenantScope?.effectiveTenantId() ?? identity.memberTenantId;
            this.setSharingTenantId(tenantId);
            if (this.form?.controls.uploads.value.length) {
              this.seedNewCalendar(this.sharingTenantId() ?? tenantId, this.event.calendarId);
            }
          }
        }
      }),
      catchError(() => of(undefined)),
    ),
    { initialValue: undefined },
  );
  protected readonly displayWorkspaceIdentity = itemDisplayIdentity(
    this.workspaceIdentity,
    inject(WorkspaceIdentityService),
    this.stitchItemChanged,
    () => this.stitchItem,
    () => this.dp,
  );

  /** Which calendar the open item is filed under; see {@link headerLocationBadge}. */
  protected readonly locationBadge = headerLocationBadge({
    changed: this.stitchItemChanged,
    containerId: () => this.stitchItem?.calendarId,
    cache: inject(CalendarSummariesCache),
    search: inject(AdvancedSearchService),
    application: Application.calendar,
    badgeType: 'calendar',
    alsoWhen: this.openedFromHub,
  });

  protected readonly footerBadge = headerLocationBadge({
    changed: this.stitchItemChanged,
    containerId: () => this.stitchItem?.calendarId,
    cache: inject(CalendarSummariesCache),
    badgeType: 'calendar',
    shown: computed(() => !!this.displayWorkspaceIdentity()),
  });

  /**
   * Bumped on every form change and every new form, so the header's when below follows the fields as they change.
   * A count, not the value: opening another event built a new form that started at `null` again, the same value as
   * before, so the header kept the previous event's time and repeat rule.
   */
  private readonly headerFormChanges = toSignal(
    this.formReady.pipe(
      switchMap((form) => form.valueChanges.pipe(startWith(null))),
      scan((count) => count + 1, 0),
    ),
    { initialValue: 0 },
  );
  private readonly headerDisplayZone = inject(DisplayTimeZoneService);
  /** An invitation's locked fields follow the display zone, which can resolve after the form opened. */
  private readonly invitationZoneSync = effect(() => {
    const zone = this.headerDisplayZone.timeZone();
    untracked(() => this.reseedInvitationForZone(zone));
  });
  /**
   * The line under the title: "October 21, 11:00 AM – 12:00 PM GMT+9 · Repeats weekly on Tuesday" (stitch-app-shell
   * `detailSub`, Johanna 2026-10-21). It follows the Starts, Ends, All day and Repeats fields as they change, and
   * reads in the account's zone as the cards and the calendar do (Johanna, 2026-10-22). That zone is named only when
   * a clock other than the browser's is involved (`headerNamesZone`, Johanna 2026-10-23).
   */
  protected readonly headerWhen = computed<string>(() => {
    this.headerFormChanges();
    const zone = this.headerDisplayZone.timeZone();
    if (!this.form) {
      return '';
    }
    try {
      return eventHeaderWhen(this.fromFormGroup(), 'en-US', zone);
    } catch {
      return '';
    }
  });
  /** The header's when, the repeat rule on a line of its own (Johanna, 2026-10-22). */
  protected readonly headerWhenLines = computed<string[]>(() => {
    const when = this.headerWhen();
    const at = when.indexOf(' · Repeats ');
    return at < 0 ? [when] : [when.slice(0, at), when.slice(at + 3)];
  });
  private readonly titleByEventsFilter = new TitleByEventsFilterPipe(inject(Injector));
  /** stitch-app-shell breadcrumbs, as in every app. */
  protected readonly navigation = new DetailNavigation(CalendarAppStateService, {
    appLabel: APP_CRUMBS.calendar,
    viewTitle: (folder) => this.titleByEventsFilter.transform(folder, null, true),
  });

  /** [workspace ›] Calendar app › the list's view › this event. */
  protected breadcrumbs(): DetailCrumb[] {
    this.workspaceRevision();
    return this.navigation.crumbs({
      workspace: itemWorkspace(this.displayWorkspaceIdentity(), this.stitchItem),
      container: this.headerCalendar()?.title,
      fallbackView: 'Events',
      itemTitle: this.event.title || '(No title)',
      itemId: this.event?.id,
    });
  }

  /**
   * The Calendar field's value as it changes: the header badge follows a calendar change at once (Johanna,
   * 2026-10-22: it kept the old calendar until a refresh, or for good, because `stitchItem` is no signal).
   */
  private readonly formCalendarId = toSignal(
    this.formReady.pipe(
      switchMap((form) => {
        const control = form.controls['calendarId'];
        return control.valueChanges.pipe(startWith(control.value));
      }),
    ),
    { initialValue: undefined },
  );

  /** The calendar this event belongs to, named by the header's container badge. */
  protected readonly headerCalendar = inject(CalendarSummariesCache).get(
    computed(() => {
      this.workspaceRevision();
      return this.formCalendarId() ?? this.stitchItem?.calendarId ?? null;
    }),
  );

  @Input() event = new CalendarEvent();

  public changesKey = 'event';
  public calendar: Calendar;
  public readonly tabs: Tab[] = [
    { title: 'Event', value: 'event' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  protected override attachmentsMenuStateVariant = 'events';

  protected get isRemoteEvent(): boolean {
    return this.event?.isRemoteEvent ?? false;
  }

  protected dragDataType = DragDataTypes.event;

  private readonly calendarsService = inject(CalendarsService);
  // #916: seeds a new item's zone from the person's own zone — the one the grid is drawn in.
  private readonly accountTimeZoneSettingsService = inject(AccountTimeZoneSettingsService);
  private readonly defaultReminderService = inject(DefaultReminderService);
  private readonly inviteConfirmationService = inject(EventInviteConfirmationService);
  private readonly recurrenceEditModalService = inject(RecurrenceEditModalService);
  private readonly toaster = inject(ToasterService);
  private readonly calendarAppStateService = inject(CalendarAppStateService);
  private readonly creationCalendarSelection = inject(CREATION_CALENDAR_SELECTION, { optional: true });

  private wasAutoApplied = false;
  private railCalendarId: string | undefined;
  private autoSeededCalendarId: string | undefined;
  private calendarSeedDraft: CalendarEvent | undefined;
  private readonly newCalendarReseed$ = new Subject<void>();
  protected readonly newCalendarStatus = signal<'loading' | 'ready' | 'empty' | 'error'>('loading');

  protected get needsNewCalendar(): boolean {
    return !this.event?.id && this.spaceContextStore.type() === SpaceType.SHARED_SPACE;
  }

  /** A saved event keeps its calendar; only a new draft follows the destination picker. */
  private seedNewCalendar(tenantId: string, preferredId?: string): void {
    this.newCalendarReseed$.next();
    if (!this.form || this.event.id) return;
    const event = this.event;
    const form = this.form;
    const hasPreferredCalendar = !!(form.controls.calendarId.value ?? preferredId);
    this.newCalendarStatus.set('loading');
    const requestContext = owningTenantContext(tenantId);
    this.calendarsService
      .search({ limit: 100, archived: false, deleted: false }, { requestContext })
      .pipe(
        take(1),
        switchMap(({ items }) => {
          const owned = items.filter((calendar) => calendar.accountId === tenantId && !calendar.readOnly);
          const selected = owned.find((calendar) => calendar.id === (form.controls.calendarId.value ?? preferredId ?? this.railCalendarId));
          const calendar = selected ?? owned.find((calendar) => calendar.default);
          // The default endpoint creates one if missing. Never call it for a workspace with no calendars.
          return calendar
            ? of(calendar)
            : owned.length
              ? this.calendarsService.getDefaultCalendar(false, { requestContext }).pipe(
                  take(1),
                  map((dto) => new Calendar(dto)),
                )
              : of(undefined);
        }),
        takeUntil(this.newCalendarReseed$),
        takeUntil(this.formRebound$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (calendar) => {
          if (this.event !== event || this.form !== form || this.event.id || this.sharingTenantId() !== tenantId)
            return;
          const valid = calendar?.id && calendar.accountId === tenantId && !calendar.readOnly;
          this.newCalendarStatus.set(valid ? 'ready' : 'empty');
          this.calendar = valid ? calendar : undefined;
          this.autoSeededCalendarId = valid && !hasPreferredCalendar ? calendar.id : undefined;
          this.event.calendarId = valid ? calendar.id : null;
          form.controls.calendarId.setValue(this.event.calendarId);
        },
        error: () => {
          if (this.event !== event || this.form !== form) return;
          this.newCalendarStatus.set('error');
          this.event.calendarId = null;
          form.controls.calendarId.setValue(null);
        },
      });
  }

  protected readonly tzSeeder = new TimeZoneSeedingController();

  protected readonly calendarSuggestions = computed<AutocompleteFactory<Calendar>>(() => {
    this.sharingTenantId();
    return this.calendarsService.getAutocompleteSuggestions({}, () => owningTenantContext(this.tenantOwnerId()));
  });

  protected setSharingTenantId(tenantId: string): void {
    if (this.saveInProgress || this.event?.id || this.event?.accountId || this.form?.controls.uploads.value.length)
      return;
    const previous = this.sharingTenantId();
    this.sharingTenantId.set(tenantId);
    if (!this.form) return;
    if (previous === tenantId && this.newCalendarStatus() === 'ready') return;
    this.newCalendarStatus.set('loading');
    const preferredId = previous === tenantId ? this.event.calendarId : undefined;
    this.event.calendarId = null;
    this.form.controls.calendarId.setValue(null);
    this.calendar = undefined;
    this.seedNewCalendar(tenantId, preferredId);
  }

  /** Calendar and event must have one owner, including a draft opened from a calendar row. */
  protected override performCreate(stitchItem: CalendarEvent): Observable<CalendarEvent> {
    const owner = this.tenantOwnerId();
    if (!owner) return super.performCreate(stitchItem);
    return this.calendarsService.getItem({ id: stitchItem.calendarId, accountId: owner }).pipe(
      take(1),
      switchMap((calendar) =>
        calendar.accountId === owner &&
        this.tenantOwnerId() === owner &&
        this.form.controls.calendarId.value === stitchItem.calendarId
          ? super.performCreate(stitchItem)
          : throwError(() => new Error('Choose a calendar in the selected workspace.')),
      ),
    );
  }

  /**
   * Live participant snapshot driving the RSVP control. `null` means "not yet
   * diverged from the input" — the computed then falls back to `event.participants`
   * so the buttons highlight on first paint (no empty-state flash). A server-pushed
   * Event.UPDATED sets a fresh array here to re-highlight without a form reload.
   */
  // Every set notifies, reset included: the instance is reused across events, and default equality
  // would swallow the reset back to null, leaving `rsvpEvent` on the previous event.
  private readonly rsvpParticipants = signal<EventParticipantDto[] | null>(null, { equal: () => false });
  private readonly rsvpChangeDetector = inject(ChangeDetectorRef);
  private rsvpReadGeneration = 0;

  /**
   * Reference-changing input for the RSVP buttons. Recomputes only when
   * `rsvpParticipants` changes (signal inputs ignore in-place mutation of the
   * shared `event` object), falling back to the input's participants until a
   * live update arrives.
   */
  protected readonly rsvpEvent = computed<RsvpTargetEvent>(() => ({
    // For a recurring occurrence the grid `id` is a composite occurrence id
    // ("<seriesUid>_<occurrenceIso>") the reply endpoint can't resolve — target
    // the real series uid instead; the occurrence is identified by occurrenceStart.
    id: this.event.recurrentSeriesId ?? this.event.id,
    accountId: this.event.accountId,
    scopeKey: this.event.id + ':' + this.event.startTime?.toISOString(),
    participants: this.rsvpParticipants() ?? this.event.participants ?? [],
  }));

  protected get offersRsvp(): boolean {
    return (
      this.event.isRemoteEvent ||
      (itemWorkspace(this.workspaceIdentity(), this.event) === 'team' &&
        !!findMyParticipant(this.rsvpEvent(), this.currentUserEmail()))
    );
  }

  /** Re-read attendance after a confirmed reply without replacing an unsaved draft. */
  protected onRsvpReplied(target: RsvpTargetEvent): void {
    if (!this.matchesRsvpTarget(target)) return;
    const item = { id: this.event.id, accountId: this.event.accountId };
    const generation = ++this.rsvpReadGeneration;
    this.eventsService
      .getItem(item, this.requestConfig())
      .pipe(
        take(1),
        takeUntil(this.alive),
        catchError(() => EMPTY),
      )
      .subscribe((saved) => {
        if (
          generation !== this.rsvpReadGeneration ||
          !this.matchesRsvpTarget(target) ||
          saved.id !== item.id ||
          saved.accountId !== item.accountId
        )
          return;
        this.mergeParticipantRsvpStatuses(saved);
        this.rsvpParticipants.set(this.event.participants ?? []);
        this.rsvpChangeDetector.markForCheck();
      });
  }

  private matchesRsvpTarget(target: RsvpTargetEvent): boolean {
    const current = this.rsvpEvent();
    return current.id === target.id && current.accountId === target.accountId && current.scopeKey === target.scopeKey;
  }

  private readonly accountService = inject(AccountService);
  private readonly currentUserEmail = toSignal(
    this.accountService.getAccount().pipe(map((account) => normalizeEmail(account.email))),
    { initialValue: null },
  );

  protected readonly titleStatusPrefix = computed(() =>
    resolveRsvpStatusPrefix(resolveMyRsvpStatus(this.rsvpEvent(), this.currentUserEmail())),
  );

  /**
   * Whether this invitation can still be answered, and if not, why — the same decision the mail
   * invitation card makes, so the two surfaces can't disagree. Read as a getter off `event` (like
   * `isRemoteEvent` in the template) because the base class mutates that object in place; a
   * `computed` would not see it.
   */
  protected get rsvpLifecycle(): EventRsvpLifecycle {
    return resolveRsvpLifecycle(this.event);
  }

  /**
   * Resolves the RSVP scope for the buttons. For a recurring event the user is
   * asked "This event / All events" (no "this and following" — it has no RSVP
   * meaning); the choice maps to this occurrence's start (single-occurrence
   * reply) or `undefined` (series-level). Non-recurring events reply
   * series-level without a prompt. Dismissing the dialog cancels the reply.
   */
  protected readonly rsvpScopeResolver = (): Observable<string | undefined> => {
    const event = this.event;
    const occurrenceStart = event.startTime?.toISOString();
    const isRecurring = !!(event.recurrence || event.recurrentSeriesId || event.recurrenceParentId);
    if (!isRecurring) {
      return of(undefined);
    }
    return this.recurrenceEditModalService
      .ask('RSVP to recurring event', [
        { mode: RecurrenceEditMode.THIS_EVENT },
        { mode: RecurrenceEditMode.ALL_EVENTS },
      ])
      .pipe(map((mode) => (mode === RecurrenceEditMode.THIS_EVENT ? occurrenceStart : undefined)));
  };

  constructor(
    private eventsService: EventsService,
    injector: Injector,
  ) {
    super(injector, eventsService);
    this.sharingTenantId.set(this.calendarTenantScope?.effectiveTenantId() ?? null);
  }

  @(Implements<OnInit>)
  public override ngOnInit(): void {
    (this.creationCalendarSelection ?? this.calendarAppStateService.getBestSelectedCalendarUid())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((calendarUid) => {
        const firstCalendar = this.calendarSeedDraft !== this.event;
        this.calendarSeedDraft = this.event;
        this.railCalendarId = calendarUid;
        if (firstCalendar && this.needsNewCalendar && this.form && !this.form.controls.calendarId.dirty &&
          (!this.event.calendarId || this.event.calendarId === this.autoSeededCalendarId)) {
          const tenantId = this.event.accountId ?? this.sharingTenantId();
          if (tenantId && calendarUid) {
            this.event.calendarId = null;
            this.form.controls.calendarId.setValue(null);
            this.seedNewCalendar(tenantId, calendarUid);
          }
        }
        // Personal windows keep their rail seed. A team draft validates the chosen owner's calendars below.
        this.seedPersonalCalendar();
      });

    super.ngOnInit();

    // Re-run for every item this reused instance is bound to, not just the first: the calendar main
    // view swaps `[event]` on the SAME `app-event-form` (Angular's `@switch`/`@case` keeps the matched
    // branch's component alive across re-evaluations), so `ngOnChanges` fires `stitchItemChanged` again
    // per item, and the base class's own subscription (registered above, in `super.ngOnInit()` — RxJS
    // Subjects notify in subscription order — so it always runs first) rebuilds `this.form` as a brand
    // new `FormGroup` each time. Every one of these binds to that specific form's own controls, so left
    // as a one-shot `ngOnInit` call they wired themselves to the FIRST event's form and went silently
    // dead — no error, just no more timezone auto-seed, default-reminder auto-apply, or hand-off-edit
    // tracking — the moment this instance was reused to open a second new event.
    this.stitchItemChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // Tears down the PREVIOUS form's own subscriptions (`watchForManualEditsDuringHandOff`,
      // `initDefaultReminder`) before rebinding to the new one — both attach with `takeUntil(this.
      // formRebound$)` below. Left only on `takeUntilDestroyed(this.destroyRef)`, every re-run would
      // add another live subscription bound to the PREVIOUS (discarded) form's controls, accumulating
      // for as long as this reused instance keeps being handed new events. `tzSeeder.start()` needs no
      // such signal — it already tears itself down on every call (see its own doc comment).
      this.formRebound$.next();
      this.autoSeededCalendarId = undefined;

      this.seedPersonalCalendar();

      // A private, prunable copy: `transferredDirtyFields` itself is owned by the base and can't be
      // mutated here, but a field the user edits in *this* editor before the calendar's zone resolves
      // must stop being hand-off-eligible immediately — see `watchForManualEditsDuringHandOff`.
      this.handOffEligibleFields = new Set(this.transferredDirtyFields ?? []);
      this.watchForManualEditsDuringHandOff();
      this.initDefaultReminder();
      if (this.needsNewCalendar) this.newCalendarStatus.set('loading');
      this.initTimeZoneSeeding();
      this.newCalendarReseed$.next();
      const tenantId = this.event.accountId ?? (this.workspaceIdentitySettled() ? this.sharingTenantId() : null);
      if (this.needsNewCalendar && tenantId) {
        this.seedNewCalendar(tenantId, this.event.calendarId);
      } else if (this.needsNewCalendar && this.workspaceIdentitySettled()) {
        this.newCalendarStatus.set('error');
      }
    });
  }

  /** Seeds each personal draft, including reused forms, without replacing an explicit choice. */
  private seedPersonalCalendar(): void {
    const control = this.form?.controls.calendarId;
    if (
      this.event.id ||
      this.needsNewCalendar ||
      this.event.calendarId ||
      control?.value ||
      control?.dirty ||
      !this.railCalendarId
    ) return;
    this.event.calendarId = this.railCalendarId;
    control?.setValue(this.railCalendarId);
  }

  /** Torn down and re-subscribed on every `stitchItemChanged` — see the subscription above. */
  private readonly formRebound$ = new Subject<void>();

  /**
   * A field the quick-form hand-off marked dirty stays eligible for the initial timezone re-seed (see
   * `reseedNewEventWallClock`) only until the user actually edits it here. `dirty` alone can't tell
   * "dirty because of the hand-off" apart from "dirty because I just typed over it" — both look
   * identical on the control — so this watches for a real value change and prunes eligibility the
   * moment one happens, before the async calendar lookup ever resolves and decides whether to convert
   * the pair. `reseedingWallClock` distinguishes a genuine edit from the very re-seed this guards,
   * which also calls `setValue` on these same controls.
   */
  private handOffEligibleFields = new Set<string>();
  private reseedingWallClock = false;

  private watchForManualEditsDuringHandOff(): void {
    if (this.handOffEligibleFields.size === 0) {
      return;
    }

    // Pairs, not individual controls: `reseedPair` treats a pair as eligible the moment *either* of its
    // two names is still in the set (that's what lets a hand-off that only dirtied one of them still
    // re-seed both), so an edit to either control has to revoke the *other* too — including one that was
    // never itself eligible — or the untouched name alone keeps the whole pair reseedable and the edit
    // gets silently overwritten along with it.
    const pairs: ReadonlyArray<readonly [string, FormControl<Date>, string, FormControl<Date>]> = [
      ['startDate', this.form.controls.startDate, 'startTime', this.form.controls.startTime],
      ['endDate', this.form.controls.endDate, 'endTime', this.form.controls.endTime],
    ];

    for (const [dateName, dateControl, timeName, timeControl] of pairs) {
      if (!this.handOffEligibleFields.has(dateName) && !this.handOffEligibleFields.has(timeName)) {
        continue;
      }

      const revokePair = (): void => {
        if (!this.reseedingWallClock) {
          this.handOffEligibleFields.delete(dateName);
          this.handOffEligibleFields.delete(timeName);
        }
      };

      dateControl.valueChanges
        .pipe(takeUntil(this.formRebound$), takeUntilDestroyed(this.destroyRef))
        .subscribe(revokePair);
      timeControl.valueChanges
        .pipe(takeUntil(this.formRebound$), takeUntilDestroyed(this.destroyRef))
        .subscribe(revokePair);
    }
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    if (changes.event && !this.event?.id) {
      const identity = this.workspaceIdentity();
      this.sharingTenantId.set(
        this.event?.accountId ?? this.calendarTenantScope?.effectiveTenantId() ?? identity?.memberTenantId ?? null,
      );
    }
    super.ngOnChanges(changes);
    if (changes.tab && this.tab === 'comments') this.openItemComments();
    if (changes.tab && this.tab === 'access' && this.insideTeam) this.openWorkspaceAccess();
    // Reset to the input's participants when this instance is reused for a
    // different event (null → the computed falls back to event.participants).
    if (this.changesKey in changes) {
      this.rsvpReadGeneration++;
      this.rsvpParticipants.set(null);
    }
  }

  /**
   * A new event inherits its timezone from the selected calendar, falling back to the
   * account/Settings timezone, then the browser, then UTC (mirrors the backend chain from
   * SBUX-767). Re-seeds when the target calendar changes, never overrides a hand-picked value, and
   * never touches existing events; the picker shows which rung the value came from. Resolution and
   * the manual-override hint live in the shared TimeZoneSeedingController.
   */
  private initTimeZoneSeeding(): void {
    const calendarTimeZone$ = this.form.controls.calendarId.valueChanges.pipe(
      startWith(this.form.controls.calendarId.value),
      // Wait for the owner's calendar before the initial zone seed; resolving settings first would
      // label unchanged wall-clock controls with a later calendar zone and move their stored instant.
      filter(() => !this.needsNewCalendar || this.newCalendarStatus() !== 'loading'),
      // Empty/cleared calendars must emit null so the controller can fall back to settings → browser → UTC;
      // only fetch a calendar for a real id.
      switchMap((calendarId) =>
        calendarId
          ? this.calendarsService.getItem({ id: calendarId, accountId: this.tenantOwnerId() }).pipe(
              take(1),
              catchError(() => of(null)),
              map((calendar) => calendar?.timeZone ?? null),
            )
          : of(null),
      ),
    );

    this.tzSeeder.start({
      control: this.form.controls.timeZone,
      destroyRef: this.destroyRef,
      isExistingEntity: !!this.event.id,
      calendarTimeZone$,
      settingsTimeZone$: this.accountTimeZoneSettingsService.timeZoneOnce(),
      // SBUX-829 — the ONE place a calendar's own timezone seeds anything: the full form's dropdown, and
      // only when the draft did NOT come from a grid cell. A cell draft must resolve the ACCOUNT zone,
      // because the grid is laid out in it and a double-click has to produce the same times as a single
      // click; seeding the calendar's zone there would show a different clock than the cell that was
      // clicked. Everywhere else (quick popover, lists, grids) the calendar's zone is ignored entirely.
      rungs: this.event.createdFromCell ? undefined : ['calendar', 'settings', 'browser', 'utc'],
      showCalendarOverrideHint: true,
      // Re-seed only on the initial resolve. A later calendar change keeps the shown numbers (and
      // just relabels the zone), matching the manual-tz-pick behaviour. (SBUX-829)
      onSeed: ({ timeZone }, isInitialSeed) => {
        if (isInitialSeed) {
          this.reseedNewEventWallClock(timeZone);
        }
      },
    });
  }

  /**
   * Re-express a pristine new event's date/time controls in the freshly resolved timezone. The form
   * is built synchronously (asFormGroup) before the async tz chain resolves, so the controls first
   * carry the browser wall clock; leaving them there makes the write path interpret those browser
   * numbers in the resolved zone and store the wrong instant — off by the browser↔resolved offset
   * (SBUX-829). The source instants are the event's own start/end (the same values asFormGroup
   * seeds from), so the shown moment is preserved and only its zone-relative wall clock changes.
   * Guarded to a pristine, non-all-day new event so it never disturbs an existing event's stored
   * time or a value the user already typed.
   *
   * A pair that arrived pre-dirtied by a quick-form hand-off (`handOffEligibleFields`, re-seeded on
   * every `stitchItemChanged` from `transferredDirtyFields`) is the one exception to the dirty guard: the user never
   * touched *this* editor's start/end pickers, so the numbers still need re-expressing once this
   * editor's own timezone resolves — otherwise they stay labelled in whatever zone the quick form
   * resolved (its own calendar lookup can land on a different value, or resolve later, than this
   * editor's) while the `timeZone` control moves on, silently shifting the saved instant and, because
   * only one side of the pair might be hand-off-dirty while the other is genuinely untouched, the
   * duration between them. `watchForManualEditsDuringHandOff` revokes that exception the moment the
   * user edits the pair here, so a real edit made during the resolve window is never overwritten.
   */
  private reseedNewEventWallClock(timeZone: string): void {
    if (this.event.id || this.form.controls.allDay.value || !isValidTimeZone(timeZone)) {
      return;
    }

    // Snapshot before re-seeding: setValue below doesn't dirty, so this stays true unless the user
    // already typed something during the async-resolve window.
    const wasPristine = this.form.pristine;

    const reseedPair = (
      dateControl: FormControl<Date>,
      timeControl: FormControl<Date>,
      instant: Date | undefined,
      fieldNames: readonly [string, string],
    ): void => {
      const isHandOffOnly = fieldNames.some((name) => this.handOffEligibleFields.has(name));

      if (!instant || ((dateControl.dirty || timeControl.dirty) && !isHandOffOnly)) {
        return;
      }
      const fakeLocal = toFakeLocal(instant, timeZone);
      this.reseedingWallClock = true;
      // `<app-date-range-picker>`'s `followStart()` ([autoAdjustEnd] on this form) only skips its
      // "shift the end to preserve the gap" cascade when the start controls are PRISTINE — its own
      // documented contract ("every programmatic write reaching here is pristine"). The hand-off-only
      // exception above breaks that contract: it lets this write proceed even though the pair is
      // already dirty (pre-dirtied by the quick-form hand-off), so the picker would otherwise read it
      // as a genuine user move and clobber the end. Restoring pristine just for the synchronous
      // duration of the write — rather than suppressing `valueChanges` outright — is what lets
      // `followStart()` still run and refresh its own baseline (`lastStart`) and derived signals off
      // this write; suppressing the event entirely starved that baseline, so the user's very next
      // start edit computed its duration against the stale PRE-reseed value (reproduced: a 30-minute
      // event became 60 minutes after moving the start forward by an hour).
      const wasDirty = dateControl.dirty || timeControl.dirty;
      try {
        if (wasDirty) {
          dateControl.markAsPristine();
          timeControl.markAsPristine();
        }
        dateControl.setValue(fakeLocal);
        timeControl.setValue(fakeLocal);
      } finally {
        if (wasDirty) {
          dateControl.markAsDirty();
          timeControl.markAsDirty();
        }
        this.reseedingWallClock = false;
      }
    };

    reseedPair(this.form.controls.startDate, this.form.controls.startTime, this.event.startTime, [
      'startDate',
      'startTime',
    ]);
    reseedPair(this.form.controls.endDate, this.form.controls.endTime, this.event.endTime, ['endDate', 'endTime']);

    // This is the only re-seed a new event ever gets (onSeed only calls in on `isInitialSeed`), so
    // hand-off eligibility has done its one job — clear it so a control that stayed dirty for some
    // other reason is never again mistaken for hand-off-only.
    this.handOffEligibleFields.clear();

    // Keep Cancel's revert baseline aligned with the re-seeded time (originalFormValue was captured
    // in ngOnInit against the pre-resolve browser wall clock). Only when the form is otherwise
    // pristine — so a title/field the user typed during the resolve window isn't baked into the
    // revert snapshot. Deliberately NOT markAsPristine (the per-control dirty guards above already
    // protect any control the user has touched).
    if (wasPristine) {
      this.originalFormValue = this.form.getRawValue();
    }
  }

  private initDefaultReminder(): void {
    if (this.event.id) {
      return;
    }

    this.defaultReminderService.defaultMinutes$
      .pipe(
        filter(() => !this.form.controls.allDay.value),
        takeUntil(this.formRebound$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const isDefaultReminderSet = this.applyDefaultReminder(this.form.controls.reminders);
        if (isDefaultReminderSet) {
          // Only the automatic reminder belongs in the baseline. Keep carried or
          // newly typed fields dirty so Add details remains immediately saveable.
          this.originalFormValue = {
            ...this.originalFormValue,
            reminders: this.form.controls.reminders.getRawValue(),
          };
        }
      });

    this.form.controls.allDay.valueChanges
      .pipe(takeUntil(this.formRebound$), takeUntilDestroyed(this.destroyRef))
      .subscribe((allDay) => {
        this.handleAllDayChange(this.form.controls.reminders, allDay);
      });
  }

  private applyDefaultReminder(reminders: FormArray<ReminderFormGroup>): boolean {
    const isDefaultReminderApplied = this.defaultReminderService.applyIfNeeded(reminders);
    if (isDefaultReminderApplied) {
      this.wasAutoApplied = true;
    }
    return isDefaultReminderApplied;
  }

  private handleAllDayChange(reminders: FormArray<ReminderFormGroup>, allDay: boolean): void {
    if (!allDay) {
      this.applyDefaultReminder(reminders);
      return;
    }

    if (!this.wasAutoApplied) {
      return;
    }

    const isReminderCleared = this.defaultReminderService.removeIfUntouched(reminders);
    if (isReminderCleared) {
      this.wasAutoApplied = false;
    }
  }

  public override get stitchItem(): CalendarEvent {
    return this.event;
  }

  private pendingRecurrenceEditMode?: RecurrenceEditMode;

  @warmUpObservable
  public override handleSubmit(): Observable<CalendarEvent> {
    if (this.needsNewCalendar && this.newCalendarStatus() !== 'ready') return EMPTY;
    if (this.pristine && this.stitchItem?.id) {
      return EMPTY;
    }

    if (!this.form.get('color')?.value && this.calendar?.color) {
      this.form.patchValue({ color: this.calendar.color }, { emitEvent: false });
    }

    // A recurrence rule is series-level, so it can't apply to a single occurrence. When the user
    // changed the recurrence control, disable "This event" (greyed + tooltip) in the scope dialog so
    // the meaningless combination can't be chosen — the recurrence change then always lands on a
    // series-level scope (All / This and following).
    // On an invitation "this and following" would fork a series cut off from the organizer, so the
    // backend refuses it; the user keeps the other two scopes.
    const recurrenceEditMode$ =
      this.event.id && this.event.recurrentSeriesId
        ? this.recurrenceEditModalService.ask('Edit recurring event', [
            { mode: RecurrenceEditMode.ALL_EVENTS },
            {
              mode: RecurrenceEditMode.THIS_AND_FOLLOWING,
              disabledReason: this.isRemoteEvent
                ? 'Not available for an event received via invitation — edit this event or all events'
                : undefined,
            },
            {
              mode: RecurrenceEditMode.THIS_EVENT,
              disabledReason: this.form.controls.recurrence.dirty
                ? 'Recurrence changes apply to the whole series, not a single occurrence'
                : undefined,
            },
          ])
        : of(undefined as RecurrenceEditMode | undefined);

    return recurrenceEditMode$.pipe(
      switchMap((mode) => {
        this.pendingRecurrenceEditMode = mode;

        return this.inviteConfirmationService.askIfNeeded({
          form: this.form,
          originalFormValue: this.originalFormValue,
          isRemoteEvent: this.isRemoteEvent,
          isExistingEvent: !!this.stitchItem?.id,
        });
      }),
      switchMap((choice) => {
        if (choice === 'send') {
          return this.performSaveAndSendInvitation();
        }
        return super.handleSubmit();
      }),
    );
  }

  protected override performUpdate(
    stitchItem: CalendarEvent,
    emitListUpdate: boolean,
    updatePayload?: StitchRequestConfig['updatePayload'],
  ): Observable<CalendarEvent> {
    return this.eventsService
      .update(
        stitchItem,
        { emit: emitListUpdate, ...this.requestConfig(), ...(updatePayload === undefined ? {} : { updatePayload }) },
        this.pendingRecurrenceEditMode,
      )
      .pipe(finalize(() => (this.pendingRecurrenceEditMode = undefined)));
  }

  private isEventFormSnapshot(value: object): value is EventFormRawValue {
    return 'participants' in value && 'title' in value;
  }

  private performSaveAndSendInvitation(): Observable<CalendarEvent> {
    // Capture the scope now — `pendingRecurrenceEditMode` is cleared by performUpdate's finalize
    // during the super.handleSubmit() below.
    const mode = this.pendingRecurrenceEditMode;
    const originalSeriesId = this.event.recurrentSeriesId;

    return super.handleSubmit().pipe(
      tap(() => {
        this.saveInProgress = true;
      }),
      switchMap((savedItem) =>
        this.sendSplitAwareInvites(savedItem, mode, originalSeriesId).pipe(
          switchMap(() => this.eventsService.getItem(savedItem)),
        ),
      ),
      take(1),
      takeUntil(this.alive),
      tap({
        next: (refreshedEvent) => {
          Object.assign(this.stitchItem, refreshedEvent);
          // Update participants - they now have SENT or SENT_PENDING status instead of PENDING
          this.form.get('participants')?.setValue(refreshedEvent.participants);
          this.form.markAsPristine();
          this.originalFormValue = this.form.getRawValue();
        },
        error: () => {
          this.toaster.show({
            text: 'Event saved but invitations failed to send. You can retry from the event.',
            icon: 'sapphire-close-small',
          });
          this.handleError();
        },
      }),
      finalize(() => {
        this.saveInProgress = false;
      }),
    );
  }

  /**
   * Send invitations after a save, handling the `this_and_following` split case.
   *
   * A split truncates the ORIGINAL series and creates a NEW one (`savedItem`).
   * Both must be notified: the new series so attendees receive its occurrences,
   * AND the original so attendees drop its now-removed tail — otherwise the stale
   * tail collides with the new series on the same dates (a duplicate on the
   * attendee's calendar). Mirrors the MCP-side dual-send; both calls reuse the
   * existing `/invite/send` endpoint (no new API).
   *
   * An "all events" save answers with the OCCURRENCE the form had open (its identity is what the form
   * keeps editing), but invitations are sent for a stored row, and the series row is what changed —
   * so that scope invites the series. Every other mode notifies the saved event itself.
   */
  private sendSplitAwareInvites(
    savedItem: CalendarEvent,
    mode: RecurrenceEditMode | undefined,
    originalSeriesId: string | undefined,
  ): Observable<void> {
    const inviteTarget =
      mode === RecurrenceEditMode.ALL_EVENTS ? (savedItem.recurrentSeriesId ?? savedItem.id) : savedItem.id;
    const config: StitchRequestConfig = { tenantItems: [savedItem] };
    const sendNew$ = this.inviteConfirmationService.sendInvites(inviteTarget, config);
    const wasSplit = mode === RecurrenceEditMode.THIS_AND_FOLLOWING;
    if (wasSplit && originalSeriesId && originalSeriesId !== savedItem.id) {
      return sendNew$.pipe(switchMap(() => this.inviteConfirmationService.sendInvites(originalSeriesId, config)));
    }
    return sendNew$;
  }

  public override delete(): void {
    if (!this.event.recurrentSeriesId) {
      return super.delete();
    }

    this.recurrenceEditModalService
      .ask('Delete recurring event')
      .pipe(
        switchMap((mode) => this.eventsService.delete({ ids: [this.stitchItem.id] }, true, mode, this.requestConfig())),
        takeUntil(this.alive),
      )
      .subscribe(() => {
        this.stitchItem.deleted = true;
      });
  }

  public override archive(): void {
    const newValue = !this.form.controls.archived.value;
    // Only archiving a recurring event prompts; unarchiving and non-recurring events keep the base flow.
    if (!newValue || !this.event.recurrentSeriesId) {
      return super.archive();
    }

    this.recurrenceEditModalService
      .ask('Archive recurring event')
      .pipe(
        switchMap((mode) =>
          this.eventsService.archive({ ids: [this.stitchItem.id] }, true, mode, this.requestConfig()),
        ),
        takeUntil(this.alive),
      )
      .subscribe(() => {
        this.stitchItem.archived = true;
      });
  }

  /**
   * Silently merge participant RSVP statuses from the backend into the current
   * form without affecting dirty state.  invitationAcceptStatus is read-only on
   * the frontend — only the backend updates it when processing ICS REPLY emails.
   *
   * @returns `true` when the backend update only changed participant RSVP
   *   statuses (no confirmation dialog needed).
   */
  protected override mergeServerManagedFields(updatedItem: CalendarEvent): boolean {
    if (!this.form) {
      return false;
    }

    // On an invitation the RSVP buttons mirror the participants: applyUpdatedItem mutates `event` in
    // place, which a signal input cannot observe — so re-highlight them off a reference-changing
    // snapshot. The content fields then go through the usual decision below: the user edits their
    // copy locally, so an organizer update arriving over a dirty form gets the "recent changes"
    // dialog like any other, and over a pristine form is applied (the organizer's update wins).
    if (this.event.isRemoteEvent) {
      this.rsvpParticipants.set(updatedItem.participants ?? []);
    }

    const onlyParticipantFields = this.isParticipantOnlyChange(updatedItem);
    if (!onlyParticipantFields) {
      return false;
    }

    // For local events, only merge RSVP statuses silently.
    this.mergeParticipantRsvpStatuses(updatedItem);

    // Same reason as the remote branch: the merge writes `event.participants` in place, which the
    // computed cannot observe.
    this.rsvpParticipants.set(this.event.participants ?? []);

    // Only participant fields changed — whether or not there was anything
    // to actually merge (e.g. duplicate signal), skip the dialog.
    return true;
  }

  /**
   * Apply backend RSVP status updates onto a participant list.
   * Only overwrites `invitationAcceptStatus`; all other fields are preserved.
   */
  private applyRsvpDeltas(
    participants: EventParticipantDto[],
    backendByEmail: ReadonlyMap<string, EventParticipantDto>,
  ): { participants: EventParticipantDto[]; changed: boolean } {
    let changed = false;
    const result = participants.map((p) => {
      const backendP = backendByEmail.get(p.email.toLowerCase());
      if (backendP && backendP.invitationAcceptStatus !== p.invitationAcceptStatus) {
        changed = true;
        return { ...p, invitationAcceptStatus: backendP.invitationAcceptStatus };
      }
      return p;
    });
    return { participants: result, changed };
  }

  private mergeParticipantRsvpStatuses(updatedItem: CalendarEvent): boolean {
    if (!updatedItem.participants?.length) {
      return false;
    }

    const participantsControl = this.form.get('participants');
    if (!participantsControl) {
      return false;
    }

    const backendByEmail = new Map(updatedItem.participants.map((p) => [p.email.toLowerCase(), p]));

    // Step 1: apply backend RSVP statuses onto current form participants
    const currentParticipants: EventParticipantDto[] = participantsControl.value || [];
    const merged = this.applyRsvpDeltas(currentParticipants, backendByEmail);
    if (!merged.changed) {
      return false;
    }

    // Step 2: update form control, preserving dirty state
    const wasDirty = participantsControl.dirty;
    participantsControl.setValue(merged.participants, { emitEvent: true });
    if (!wasDirty) {
      participantsControl.markAsPristine();
    }

    // Step 3: update saved model
    this.event.participants = merged.participants;

    // Step 4: apply same deltas onto cancel baseline (not form value)
    // so Cancel reverts user's unsaved add/remove but keeps RSVP updates
    const baselineParticipants: EventParticipantDto[] =
      (this.originalFormValue && this.isEventFormSnapshot(this.originalFormValue)
        ? this.originalFormValue.participants
        : null) || [];
    const updatedBaseline = this.applyRsvpDeltas(baselineParticipants, backendByEmail);
    this.originalFormValue = { ...this.originalFormValue, participants: updatedBaseline.participants };

    return true;
  }

  /**
   * Backend-only fields whose changes should not trigger the confirmation
   * dialog (e.g. participant RSVP statuses updated via ICS REPLY).
   * Typed against the payload shape so typos are caught at compile time.
   */
  private static readonly IGNORABLE_CHANGE_FIELDS: ReadonlySet<keyof ReturnType<CalendarEvent['asPayloadJSON']>> =
    new Set(['participants'] as const);

  /**
   * Check whether the backend update only affected participant-level data
   * (RSVP statuses) by comparing event fields against the saved model.
   *
   * Dynamically diffs the payload representations — adding a new field to
   * asPayloadJSON does not require updating a manual field list here.
   *
   * For local events, also verify the participant email sets match.
   */
  private isParticipantOnlyChange(updatedItem: CalendarEvent): boolean {
    const current = this.event;

    const currentPayload = current.asPayloadJSON();
    const updatedPayload = updatedItem.asPayloadJSON();

    const hasNonIgnorableChange = Object.keys(updatedPayload).some((key) => {
      if (EventFormComponent.IGNORABLE_CHANGE_FIELDS.has(key as keyof typeof updatedPayload)) {
        return false;
      }
      return !Objects.equals(updatedPayload[key], currentPayload[key]);
    });
    if (hasNonIgnorableChange) {
      return false;
    }

    // Remote events: all participant mutations are ICS-driven, skip set comparison.
    if (current.isRemoteEvent) {
      return true;
    }

    // Local events: verify no participants were added or removed on the backend
    // side. Compare against the saved model, not the form (user may have unsaved
    // participant edits that shouldn't trigger the confirmation dialog).
    const savedEmails = new Set((current.participants || []).map((p: EventParticipantDto) => p.email.toLowerCase()));
    const backendEmails = new Set((updatedItem.participants || []).map((p) => p.email.toLowerCase()));

    if (savedEmails.size !== backendEmails.size) {
      return false;
    }
    for (const email of savedEmails) {
      if (!backendEmails.has(email)) {
        return false;
      }
    }

    return true;
  }

  /**
   * An occurrence — virtual or materialized — shows its series' fields, so an update addressed to the
   * series parent (an "all events" edit made anywhere, another browser included) is an update to this
   * form too. The base then re-fetches by this form's OWN composite id, so the occurrence refreshes in
   * place and keeps the identity its next save needs.
   */
  protected override isUpdateForThisItem(payload: { readonly ids: string[]; readonly spaceId?: string }): boolean {
    if (payload.spaceId && this.event.accountId && payload.spaceId !== this.event.accountId) return false;
    const matches = CalendarUtils.isUpdateAddressedToEvent(payload.ids, this.event);
    if (matches) this.rsvpReadGeneration++;
    return matches;
  }

  protected override tenantOwnerId(): string | undefined {
    return (
      this.stitchItem?.accountId ??
      (this.needsNewCalendar && this.workspaceIdentitySettled() ? (this.sharingTenantId() ?? undefined) : undefined)
    );
  }

  protected override requestConfig(): StitchRequestConfig {
    if (this.stitchItem?.accountId)
      return {
        tenantItems: [this.stitchItem],
        requestContext: owningTenantContext(this.stitchItem.accountId)?.set(
          EVENT_FORM_READ_CANCELLED,
          this.formRebound$,
        ),
      };
    const tenantId = this.needsNewCalendar && this.workspaceIdentitySettled() ? this.sharingTenantId() : null;
    return tenantId ? { requestContext: owningTenantContext(tenantId) } : super.requestConfig();
  }

  protected override shouldRefreshList(prev, current): boolean {
    return CalendarEvent.shouldRefreshList(prev, current);
  }

  protected override fromFormGroup(): CalendarEvent {
    const event = CalendarEvent.fromFormGroup(this.form);
    // Preserve remoteUid from the original event — the form doesn't carry it, but asPayloadJSON()
    // needs it to send only the fields an invitee may edit on a remote event.
    event.remoteUid = this.event.remoteUid;
    // An invitation's times are the organizer's and locked: keep its instants, which the wall-clock round trip
    // through the controls moves an hour early when the start falls in the display zone's repeated hour.
    if (this.isRemoteEvent) {
      event.startTime = this.event.startTime;
      event.endTime = this.event.endTime;
    }
    return event;
  }

  protected override formValueFromItem(): ReturnType<FormGroup['getRawValue']> {
    return this.event.asFormGroup(this.formGroupOptions()).getRawValue();
  }

  private formGroupOptions(): CalendarEventFormGroupOptions {
    return { maxAttachmentSize: this.maxUploadSize(), displayTimeZone: this.headerDisplayZone.timeZone() };
  }

  /** An invitation's date/time and zone controls: the organizer's, locked, so re-seeding them overwrites no edit. */
  private static readonly INVITATION_LOCKED_CONTROLS = [
    'startDate',
    'startTime',
    'endDate',
    'endTime',
    'timeZone',
  ] as const;

  /** Re-expresses an invitation's locked date/time and zone controls in the new display zone. */
  private reseedInvitationForZone(displayTimeZone: string): void {
    if (!this.form || !this.isRemoteEvent || this.form.controls.timeZone.value === displayTimeZone) {
      return;
    }
    const controls = this.form.controls;
    if (EventFormComponent.INVITATION_LOCKED_CONTROLS.some((name) => controls[name].dirty)) {
      return;
    }
    const seeded = this.event.asFormGroup({ displayTimeZone }).controls;
    const lockedValues = {
      startDate: seeded.startDate.value,
      startTime: seeded.startTime.value,
      endDate: seeded.endDate.value,
      endTime: seeded.endTime.value,
      timeZone: displayTimeZone,
    };
    this.form.patchValue(lockedValues);
    // Only the locked fields move into the baselines, so an edit already made elsewhere stays dirty and saveable.
    this.originalFormValue = { ...this.originalFormValue, ...lockedValues };
    this.captureSaveBaseline(['startTime', 'endTime', 'timeZone']);
  }

  protected override asFormGroup(): FormGroup<CalendarEventGroupConfig> {
    const form = this.event.asFormGroup(this.formGroupOptions());

    // Cap attendees at send time. skipped for remote/read-only events so their
    // pre-existing large lists stay saveable. Attached here where the control is created.
    form.controls.participants.addValidators(participantCapValidator(() => this.isRemoteEvent));
    form.controls.participants.updateValueAndValidity({ emitEvent: false });

    // Programmatically disable controls whose components use [formControl] binding
    // where template [disabled] conflicts with ControlValueAccessor.
    if (this.isRemoteEvent) {
      form.controls.allDay.disable({ emitEvent: false });
    }

    return form;
  }
  protected get insideTeam(): boolean {
    return !!itemWorkspace(this.displayWorkspaceIdentity(), this.event);
  }

  protected get workspaceFooterInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.event,
      noun: 'Events',
      collection: this.footerBadge()?.name,
      archived: this.form?.controls.archived.value ?? false,
      deleted: this.form?.controls.deleted.value ?? false,
    };
  }
  protected get workspaceLabelInputs(): Record<string, unknown> {
    return { identity: this.displayWorkspaceIdentity(), revision: this.workspaceRevision(), item: this.event };
  }
  protected readonly openWorkspaceAccess = (): void => {
    this.selectedTab = 'access';
  };

  protected get workspaceAccessInputs(): Record<string, unknown> {
    return { identity: this.displayWorkspaceIdentity(), revision: this.workspaceRevision(), item: this.event };
  }

  protected get workspaceChipInputs(): Record<string, unknown> {
    return { ...this.workspaceAccessInputs, openAccess: this.openWorkspaceAccess, compact: this.contracted };
  }

  protected get itemChangesInputs(): Record<string, unknown> | undefined {
    const item = this.stitchItem;
    if (
      !item?.id ||
      !item.accountId ||
      this.spaceContextStore.type() !== SpaceType.SHARED_SPACE ||
      item.accountId !== this.spaceContextStore.contextId()
    )
      return undefined;
    return { path: '/api/calendar/events', itemId: item.recurrentSeriesId ?? item.id, tenantId: item.accountId };
  }

  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly workspaceTabControl = viewChild<TabsComponent<string>>('workspaceTabControl');
  protected readonly footerActivityPresentation = computed(
    () =>
      !!this.hostElement.nativeElement.closest('app-full-form-modal') ||
      !this.hostElement.nativeElement.closest('app-dynamic-panel'),
  );
  private readonly footerTarget = computed(() => {
    this.workspaceRevision();
    const item = this.stitchItem;
    const activity = this.itemChangesInputs;
    return JSON.stringify([
      item?.accountId,
      item?.itemType,
      item?.id,
      activity?.['path'],
      activity?.['itemId'],
      activity?.['tenantId'],
    ]);
  });
  protected readonly footerTab = linkedSignal<ItemFooterTab>(() => {
    this.footerTarget();
    return 'comments';
  });
  private readonly showFooterActivity = linkedSignal(() => {
    this.footerTarget();
    return false;
  });
  protected readonly selectFooterTab = (tab: ItemFooterTab): void => {
    this.footerTab.set(tab);
    if (tab === 'comments') this.showFooterActivity.set(false);
  };
  private resyncingWorkspaceTab = false;

  protected selectWorkspaceTab(value: string): void {
    // handleSelect emits the retained main value again; do not clear the footer reveal on that emission.
    if (this.resyncingWorkspaceTab) return;
    const tabs = this.workspaceTabs;
    const target = tabs.find((tab) => tab.value === value);
    if (!target || target.disabled) return;
    if (value !== 'activity' || !this.footerActivityPresentation()) {
      this.showFooterActivity.set(false);
      this.selectedTab = value;
      return;
    }
    const retainedIndex = tabs.findIndex((tab) => tab.value === this.selectedTab && !tab.disabled);
    const control = this.workspaceTabControl();
    if (!this.itemChangesInputs || retainedIndex < 0 || !control) return;
    this.footerTab.set('activity');
    this.showFooterActivity.set(true);
    this.resyncingWorkspaceTab = true;
    try {
      control.handleSelect(retainedIndex);
    } finally {
      this.resyncingWorkspaceTab = false;
    }
  }

  protected get workspaceTabs(): Tab[] {
    const tabs = this.tabsWithCounts().flatMap((tab) =>
      tab.value === 'comments' && this.itemChangesInputs ? [tab, { title: 'Activity', value: 'activity' }] : [tab],
    );
    return this.insideTeam && this.selectedTab === 'access' ? [...tabs, { title: 'Access', value: 'access' }] : tabs;
  }
  protected readonly openItemComments = (): void => {
    this.showFooterActivity.set(false);
    this.selectedTab = 'comments';
  };

  protected get quickCommentInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.event,
      noun: 'event',
      selectedTab: this.selectedTab,
      openComments: this.openItemComments,
      activityComponent: this.footerActivityPresentation() ? this.workspaceComponents['app-item-changes'] : undefined,
      activityInputs: this.footerActivityPresentation() ? this.itemChangesInputs : undefined,
      footerTab: this.footerTab(),
      showActivity: this.showFooterActivity(),
      onFooterTabChange: this.selectFooterTab,
    };
  }
}
